import React, { useEffect, useState } from 'react';
import { Button, Menu, MenuItem, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { dateFormate } from '../../../utils/helper';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, InputAdornment } from '@mui/material';

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

type PresetOption = {
  label: string;
  range: () => DateRange;
};

interface DateRangePickerProps {
  onApply: (range: DateRange) => void;
  presets?: PresetOption[];
  buttonLabel?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

const defaultPresets: PresetOption[] = [
  {
    label: 'Today',
    range: () => {
      const today = new Date();
      return { startDate: today, endDate: today };
    },
  },
  {
    label: 'Yesterday',
    range: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: 'Last 7 days',
    range: () => {
      const today = new Date();
      const lastWeek = new Date();
      lastWeek.setDate(today.getDate() - 6);
      return { startDate: lastWeek, endDate: today };
    },
  },
  {
    label: 'Last 30 days',
    range: () => {
      const today = new Date();
      const lastMonth = new Date();
      lastMonth.setDate(today.getDate() - 29);
      return { startDate: lastMonth, endDate: today };
    },
  },
];

const DateRangeFilter: React.FC<DateRangePickerProps> = ({
  onApply,
  presets = defaultPresets,
  buttonLabel = 'Select Date Range',
  startDate = null,
  endDate = null,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  useEffect(() => {
    if (startDate) setFromDate(new Date(startDate));
    else setFromDate(null);
  }, [startDate]);

  useEffect(() => {
    if (endDate) setToDate(new Date(endDate));
    else setToDate(null);
  }, [endDate]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedPreset(null);
  };

  const handleApply = () => {
    onApply({ startDate: fromDate, endDate: toDate });
    handleClose();
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    onApply({ startDate: null, endDate: null });
    handleClose();
  };

  const applyPreset = (range: DateRange, label: string) => {
    setFromDate(range.startDate);
    setToDate(range.endDate);
    setSelectedPreset(label);
  };

  const isValidDate = (date: Date | null) =>
    date instanceof Date && !isNaN(date.getTime());


  const formatDateRange = (from: Date | null, to: Date | null): string => {
    if (!isValidDate(from) || !isValidDate(to)) return '';

    const sameDay = from.toDateString() === to.toDateString();
    const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
    const sameYear = from.getFullYear() === to.getFullYear();

    const format = new Intl.DateTimeFormat('default', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const formatNoYear = new Intl.DateTimeFormat('default', {
      day: 'numeric',
      month: 'short',
    });

    if (sameDay) {
      return format.format(from);
    } else if (sameMonth) {
      return `${from.getDate()} - ${format.format(to)}`;
    } else if (sameYear) {
      return `${formatNoYear.format(from)} - ${format.format(to)}`;
    } else {
      return `${format.format(from)} - ${format.format(to)}`;
    }
  };
  // const displayLabel =
  //   fromDate && toDate && startDate && endDate ? formatDateRange(fromDate, toDate) : buttonLabel;

  const formatSingleDate = (date: Date) =>
    isValidDate(date)
      ? new Intl.DateTimeFormat('default', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(date)
      : '';

  const displayLabel = (() => {
    if (isValidDate(fromDate) && isValidDate(toDate))
      return formatDateRange(fromDate, toDate);

    if (isValidDate(fromDate)) return formatSingleDate(fromDate);
    if (isValidDate(toDate)) return formatSingleDate(toDate);

    return buttonLabel;
  })();



  const isSingleDay = fromDate && toDate && fromDate.toDateString() === toDate.toDateString();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => handleOpen(e)}
          endIcon={fromDate && toDate ? null : undefined}
          sx={{
            fontSize: { xs: '11px', sm: 'auto', md: 'auto' },
            lineHeight: { xs: '1.75', sm: 'auto' },
          }}
        >
          {displayLabel}
        </Button>
        {/* {fromDate && toDate && startDate && endDate && ( */}
        {(fromDate || toDate) && (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{ ml: -1, mt: '2px' }}
            aria-label="Clear date range"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <Box sx={{ display: 'flex', px: 2, py: 1, minWidth: 300 }}>
          <Box>
            <Typography fontWeight={600} mb={1}>
              Select
            </Typography>
            {presets.map((preset) => (
              <MenuItem
                key={preset.label}
                onClick={() => applyPreset(preset.range(), preset.label)}
                selected={selectedPreset === preset.label}
              >
                {preset.label}
              </MenuItem>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isSingleDay ? (
            <DatePicker
              label="Date"
              value={fromDate}
              onChange={(date) => {
                setFromDate(date);
                setToDate(date);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: '140px' },
                  InputLabelProps: { shrink: true },
                  error: false,
                  helperText: '',
                },
              }}
            />
          ) : (
            <>
              <DatePicker
                label="After"
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: '140px' },
                    InputLabelProps: { shrink: true },
                    error: false,
                    helperText: '',
                  },
                }}
              // renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="Before"
                value={toDate}
                onChange={(date) => setToDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: '140px' },
                    InputLabelProps: { shrink: true },
                    error: false,
                    helperText: '',
                  },
                }}
              // renderInput={(params) => <TextField {...params} />}
              />
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleClear}>Clear all</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={handleApply}>
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Menu>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;
